import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Youtube, Linkedin, ChevronRight, Loader2, Mail, Phone } from 'lucide-react';
import { footerLinks } from '../data/mock';
import { toast } from '../hooks/use-toast';
import { subscribeEmail, getContactSettings } from '../services/api';
import SignInWidget from './SignInWidget';

const XIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const Footer = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [contact, setContact] = useState({ email: 'info@emeraldcorporates.com', phone: '', address_lines: ['Office Gold, Building 7, Floor 5,', '566 Chiswick High Road, Chiswick Business Park,', 'London, United Kingdom, W4 5YG'] });
  useEffect(() => { getContactSettings().then(setContact).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || loading) return;
    setLoading(true);
    try {
      await subscribeEmail(email);
      toast({
        title: 'Subscribed successfully',
        description: `${email} will now receive our latest insights.`,
      });
      setEmail('');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        toast({
          title: 'Already subscribed',
          description: 'This email is already on our list.',
        });
      } else {
        toast({
          title: 'Subscription failed',
          description: 'Please check your email and try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const socials = [
    { Icon: Facebook, href: 'https://www.facebook.com/profile.php?id=61594110496786', label: 'Facebook' },
    { Icon: Instagram, href: 'https://www.instagram.com/emeraldcorporates/', label: 'Instagram' },
    { Icon: XIcon, href: 'https://x.com/EmeraldCorpratz', label: 'X' },
    { Icon: Youtube, href: 'https://www.youtube.com/@EmeraldCorporates', label: 'YouTube' },
    { Icon: Linkedin, href: 'https://www.linkedin.com/in/emerald-corporates-31879b393/', label: 'LinkedIn' },
  ];

  return (
    <footer className="bg-[#0b1a2e] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div>
            <div className="bg-white inline-flex items-center justify-center px-5 py-3 rounded-lg">
              <img
                src="/emerald-logo.png"
                alt="Emerald Corporates"
                width={170}
                height={114}
                className="h-14 w-auto object-contain"
                data-testid="footer-logo"
              />
            </div>
            <p className="mt-6 text-sm text-white/60 leading-relaxed max-w-xs">
              EMERALD CORPORATES LIMITED is a global conglomerate with international
              alliances and core strengths across multiple services, verticals and
              operations.
            </p>
            <div className="mt-5 space-y-1 text-xs text-white/50 leading-relaxed max-w-xs">
              <div>Company No: 16120279</div>
              {(contact.address_lines || []).map((line, i) => (<div key={i}>{line}</div>))}
              {contact.email && (
                <a href={`mailto:${contact.email}`} data-testid="footer-email" className="inline-flex items-center gap-1.5 mt-1 text-white/70 hover:text-white">
                  <Mail className="w-3 h-3" /> {contact.email}
                </a>
              )}
              {contact.phone && (
                <div><a href={`tel:${contact.phone.replace(/[^+\d]/g, '')}`} data-testid="footer-phone" className="inline-flex items-center gap-1.5 text-white/70 hover:text-white">
                  <Phone className="w-3 h-3" /> {contact.phone}
                </a></div>
              )}
            </div>
            <div className="flex items-center gap-3 mt-6">
              {socials.map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 rounded-full bg-black/40 border border-white/10 flex items-center justify-center hover:bg-[#0f5c3d] hover:border-[#0f5c3d] transition-colors"
                  aria-label={label}
                  title={label}
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Business Verticals</h4>
            <ul className="space-y-3">
              {footerLinks.services.map((l) => (
                <li key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Subscribe */}
          <div>
            <h4 className="text-lg font-semibold mb-5">Subscribe</h4>
            <p className="text-sm text-white/60 mb-4">
              Stay updated with our latest news and insights delivered to your
              inbox.
            </p>
            <form
              onSubmit={submit}
              className="border border-white/15 rounded-lg p-3 bg-black/20"
            >
              <input
                type="email"
                required
                placeholder="Enter Your Mail ID"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white text-[#0b1a2e] rounded-md px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0f5c3d]"
              />
              <button
                type="submit"
                disabled={loading}
                className="mt-3 inline-flex items-center gap-1 text-[#4ade80] hover:text-[#86efac] text-lg font-semibold transition-colors disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" /> Subscribing
                  </>
                ) : (
                  <>
                    Subscribe <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-white/50">
          <span>© {new Date().getFullYear()} Emerald Corporates Limited. All rights reserved.</span>
          <div className="flex items-center gap-4 md:gap-6 flex-wrap justify-center">
            <SignInWidget variant="pill" />
            <Link to="/privacy" className="hover:text-white transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="hover:text-white transition-colors">
              Terms of Service
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
